import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-named-as-default
import ReactCrop from 'react-image-crop';
import { useClasses } from '../../../utils/overrides';
import Modal from '../Modal';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'ModalCrop');
function ModalCrop(_ref) {
  let {
    aspect,
    confirmText,
    cancelText,
    classes: classesProp,
    file,
    isOpen,
    onCancel,
    onAccept,
    title,
    overrides,
    ...props
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  const [crop, setCrop] = useState({});
  const imgRef = useRef();
  const src = useMemo(() => file && URL.createObjectURL(file) || null, [file]);
  const handleOnConfirm = useCallback(() => {
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    const hasExtension = file.name.split('.').length > 1;
    let fileName = '';
    let fileExt = '';
    if (hasExtension) {
      fileName = file.name.substr(0, file.name.lastIndexOf('.'));
      fileExt = file.name.split('.').pop();
    } else {
      fileName = file.name;
    }
    ctx.drawImage(imgRef.current, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
    canvas.toBlob(blob => {
      let newFileName = '';
      if (hasExtension) {
        newFileName = fileName.includes('_crop') ? `${fileName}.${fileExt}` : `${fileName}_crop.${fileExt}`;
      } else {
        newFileName = fileName.includes('_crop') ? `${fileName}` : `${fileName}_crop`;
      }
      blob.name = newFileName;
      onAccept(blob);
    }, file.type, 1);
  }, [crop.height, crop.width, crop.x, crop.y, file, onAccept]);
  const onImageLoaded = useCallback(img => {
    img = img.target;
    imgRef.current = img;
    const imageAspect = img.width / img.height;
    let width;
    let height;
    if (aspect && imageAspect > 1) {
      // Image landscape & aspect
      if (aspect > 1) {
        // Crop landscape
        if (imageAspect < aspect) {
          // Crop more landscape than image
          width = img.width * 0.6;
          height = width / aspect;
        } else {
          // Crop less landscape or equal than image
          height = img.height * 0.6;
          width = height * aspect;
        }
      } else {
        // Crop Portrait or square
        height = img.height * 0.6;
        width = height * aspect;
      }
    } else if (aspect) {
      // Image Portrait or square & aspect
      if (aspect < 1) {
        // Crop Portrait
        if (imageAspect > aspect) {
          // Crop more landscape than image
          height = img.height * 0.6;
          width = height / aspect;
        } else {
          // Crop less landscape or equal than image
          width = img.width * 0.6;
          height = width / aspect;
        }
      } else {
        // Crop landscape or square
        width = img.width * 0.6;
        height = width / aspect;
      }
    } else if (imageAspect > 1) {
      // Image landscape
      width = img.height * 0.6;
      height = img.height * 0.6;
    } else {
      // Image Portrait or square
      width = img.width * 0.6;
      height = img.width * 0.6;
    }
    setCrop({
      x: (img.width - width) / 2,
      y: (img.height - height) / 2,
      width,
      height,
      unit: 'px'
    });
    return false;
  }, [aspect]);
  return /*#__PURE__*/React.createElement(Modal, _extends({
    cancelText: cancelText,
    confirmText: confirmText,
    className: classes.cropModal,
    title: title,
    isOpen: isOpen,
    onCancel: onCancel,
    onConfirm: handleOnConfirm,
    useCornerClose: false,
    overrides: overrides
  }, props), /*#__PURE__*/React.createElement("div", {
    className: classes.cropModalContent
  }, src && /*#__PURE__*/React.createElement(ReactCrop, {
    crop: crop,
    aspect: aspect,
    onChange: setCrop,
    className: classes.cropCanvas
  }, /*#__PURE__*/React.createElement("img", {
    className: "ReactCrop__image",
    src: src,
    onLoad: onImageLoaded,
    alt: ""
  }))));
}
process.env.NODE_ENV !== "production" ? ModalCrop.propTypes = {
  /** Aspect ratio for the crop (width/height). When provided, constrains the crop box to this ratio. */
  aspect: PropTypes.number,
  /** Text for the confirm button in the modal footer */
  confirmText: PropTypes.string,
  /** Text for the cancel button in the modal footer */
  cancelText: PropTypes.string,
  /** Custom classes object for custom styling */
  classes: PropTypes.object,
  /** File object or string URL of the image to be cropped */
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /** Controls whether the modal is displayed */
  isOpen: PropTypes.bool,
  /** Function called when the cancel button is clicked or the modal is closed */
  onCancel: PropTypes.func,
  /** Function called when the confirm button is clicked, receives the cropped image as a Blob */
  onAccept: PropTypes.func,
  /** Title text displayed in the modal header */
  title: PropTypes.string,
  /** Object with custom style overrides */
  overrides: PropTypes.object
} : void 0;
export default /*#__PURE__*/React.memo(ModalCrop);